

import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Button,
  CircularProgress,
  TextField,
  IconButton,
  AppBar,
  Toolbar,
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper,
  useMediaQuery,
  useTheme,
  Divider,
} from "@mui/material";
import {
  Home as HomeIcon,
  Delete as DeleteIcon,
  Event as EventIcon,
  EmojiEvents as EmojiEventsIcon,
  Chat as ChatIcon,
  VideoLibrary as VideoLibraryIcon,
  Note as NoteIcon,
  ExitToApp as ExitToAppIcon,
  Menu as MenuIcon,
  ChevronRight as ChevronRightIcon,
  Upload as UploadIcon,
} from "@mui/icons-material";
import api from "../utils/api";
import { useAuth } from "./auth/AuthContext";
import AOS from "aos";
import "aos/dist/aos.css";
import AdminPanelSettings from "@mui/icons-material/AdminPanelSettings";
import SearchIcon from "@mui/icons-material/Search";
import { Container } from "@mui/material";

const AdminDashboard = () => {
  const navigate = useNavigate();
  const { logout } = useAuth();
  const [schools, setSchools] = useState([]);
  const [filteredSchools, setFilteredSchools] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" });
  const [mobileOpen, setMobileOpen] = useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  useEffect(() => {
    AOS.init({ duration: 800, easing: "ease-in-out", once: true });
  }, []);

  const fetchSchools = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await api.get("/api/schools/admin/schools");
      setSchools(response.data);
      setFilteredSchools(response.data);
    } catch (error) {
      setError(error.response?.data?.message || "Failed to load schools");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSchools();
  }, []);

  useEffect(() => {
    if (searchQuery === "") {
      setFilteredSchools(schools);
    } else {
      const filtered = schools.filter((school) =>
        Object.values(school).some(
          (value) =>
            value &&
            value.toString().toLowerCase().includes(searchQuery.toLowerCase())
        )
      );
      setFilteredSchools(filtered);
    }
  }, [searchQuery, schools]);

  const handleSort = (key) => {
    let direction = "asc";
    if (sortConfig.key === key && sortConfig.direction === "asc") {
      direction = "desc";
    }
    setSortConfig({ key, direction });
    const sortedData = [...filteredSchools].sort((a, b) => {
      if (a[key] < b[key]) return direction === "asc" ? -1 : 1;
      if (a[key] > b[key]) return direction === "asc" ? 1 : -1;
      return 0;
    });
    setFilteredSchools(sortedData);
  };

  const handleLogout = async () => {
    await logout();

    navigate("/government-login");
  };

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const drawer = (
    <Box sx={{ width: 250, bgcolor: "background.paper", height: "100%" }}>
      <Box
        sx={{
          p: 2,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          bgcolor: "#6200ee",
          color: "white",
        }}
      >
        <AdminPanelSettings sx={{ mr: 1 }} />
        <Typography variant="h6" sx={{ fontWeight: "bold" }}>
          Admin Portal
        </Typography>
      </Box>

      <Divider />
      <List>
        {[
          { text: "Dashboard", icon: <HomeIcon />, path: "/admin-dashboard" },
          {
            text: "Deleted Schools",
            icon: <DeleteIcon />,
            path: "/deleted-schools",
          },
          {
            text: "National Events",
            icon: <EventIcon />,
            path: "/amplified-events",
          },
          {
            text: "Scholarships",
            icon: <EmojiEventsIcon />,
            path: "/scholarships",
          },
          {
            text: "Support",
            icon: <ChatIcon />,
            path: "/admin-support-dashboard",
          },
          {
            text: "Upload Video",
            icon: <UploadIcon />,
            path: "/admin-video-upload",
          },
          {
            text: "My Videos",
            icon: <VideoLibraryIcon />,
            path: "/admin-videos",
          },
          {
            text: "Teacher Resources",
            icon: <NoteIcon />,
            path: "/upload-teacher-resource",
          },
          {
            text: "Logout",
            icon: <ExitToAppIcon />,
            path: "/government-login",
            action: handleLogout,
          },
        ].map((item, index) => (
          <ListItem
            button
            key={index}
            onClick={() => (item.action ? item.action() : navigate(item.path))}
            sx={{
              "&:hover": { bgcolor: "#f5f5f5" },
              borderRadius: 1,
              mx: 1,
              my: 0.5,
            }}
          >
            <ListItemIcon sx={{ color: "#6200ee" }}>{item.icon}</ListItemIcon>
            <ListItemText
              primary={item.text}
              primaryTypographyProps={{ fontWeight: "medium" }}
            />
          </ListItem>
        ))}
      </List>
    </Box>
  );

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          height: "100vh",
          bgcolor: "#f8f9fa",
        }}
      >
        <CircularProgress size={60} sx={{ color: "#9C27B0" }} />
        <Typography
          variant="h6"
          sx={{ mt: 3, color: "#2c3e50", fontWeight: "bold" }}
        >
          Loading admin dashboard...
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          height: "100vh",
          bgcolor: "#f8f9fa",
        }}
      >
        <Typography
          variant="h6"
          color="error"
          sx={{ mb: 3, textAlign: "center" }}
        >
          {error}
        </Typography>
        <Button
          variant="contained"
          onClick={fetchSchools}
          sx={{
            bgcolor: "#9C27B0",
            "&:hover": { bgcolor: "#7B1FA2" },
            px: 4,
            py: 1.5,
          }}
        >
          Try Again
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ display: "flex", minHeight: "100vh", bgcolor: "#f8f9fa" }}>
      <AppBar
        position="fixed"
        sx={{
          zIndex: theme.zIndex.drawer + 1,
          bgcolor: "white",
          color: "#2c3e50",
          boxShadow: "0 2px 10px rgba(0,0,0,0.1)",
        }}
      >
        <Toolbar>
          <IconButton
            color="inherit"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2, display: { md: "none" }, color: "#6200ee" }}
          >
            <MenuIcon />
          </IconButton>
          <Box sx={{ display: "flex", alignItems: "center", flexGrow: 1 }}>
            <AdminPanelSettings
              sx={{ color: "#6200ee", mr: 1, fontSize: 32 }}
            />
            <Typography
              variant="h6"
              sx={{ fontWeight: "bold", color: "#2c3e50" }}
            >
              Government Admin Portal
            </Typography>
          </Box>
        </Toolbar>
      </AppBar>
      <Box component="nav" sx={{ width: { md: 250 }, flexShrink: { md: 0 } }}>
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{ keepMounted: true }}
          sx={{
            display: { xs: "block", md: "none" },
            "& .MuiDrawer-paper": { boxSizing: "border-box", width: 250 },
          }}
        >
          {drawer}
        </Drawer>
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: "none", md: "block" },
            "& .MuiDrawer-paper": {
              boxSizing: "border-box",
              width: 250,
              borderRight: "none",
            },
          }}
          open
        >
          {drawer}
        </Drawer>
      </Box>
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 3,
          width: { md: `calc(100% - 250px)` },
          marginTop: "64px",
          bgcolor: "#f8f9fa",
        }}
      >
        <Container maxWidth="xl" sx={{ py: 2 }} data-aos="fade-down">
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 4,
            }}
          >
            <Typography
              variant="h4"
              sx={{ fontWeight: "bold", color: "#2c3e50" }}
            >
              Registered Schools
            </Typography>
            <Box sx={{ display: "flex", gap: 2 }}>
              <Button
                variant="contained"
                onClick={() => navigate("/deleted-schools")}
                sx={{ bgcolor: "#6200ee", "&:hover": { bgcolor: "#7f39fb" } }}
              >
                Deleted Schools
              </Button>
              <Button
                variant="contained"
                onClick={() => navigate("/amplified-events")}
                sx={{ bgcolor: "#6200ee", "&:hover": { bgcolor: "#7f39fb" } }}
              >
                National Events
              </Button>
              <Button
                variant="contained"
                onClick={() => navigate("/scholarships")}
                sx={{ bgcolor: "#6200ee", "&:hover": { bgcolor: "#7f39fb" } }}
              >
                Scholarships
              </Button>
            </Box>
          </Box>
          <Box sx={{ mb: 3, position: "relative" }}>
            <TextField
              fullWidth
              label="Search schools..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              InputProps={{
                endAdornment: <SearchIcon sx={{ color: "#666" }} />,
              }}
              sx={{
                bgcolor: "white",
                borderRadius: 1,
                boxShadow: "0 2px 10px rgba(0,0,0,0.1)",
              }}
            />
          </Box>
          <Box sx={{ display: "flex", gap: 1, mb: 3, flexWrap: "wrap" }}>
            {[
              { key: "schoolName", label: "Name" },
              { key: "location", label: "Location" },
              { key: "createdAt", label: "Date" },
              { key: "isActive", label: "Status" },
            ].map((item) => (
              <Button
                key={item.key}
                variant={sortConfig.key === item.key ? "contained" : "outlined"}
                onClick={() => handleSort(item.key)}
                sx={{
                  bgcolor: sortConfig.key === item.key ? "#9C27B0" : "#eee",
                  color: sortConfig.key === item.key ? "white" : "#666",
                  "&:hover": {
                    bgcolor: sortConfig.key === item.key ? "#7B1FA2" : "#ddd",
                  },
                  borderRadius: 1,
                }}
              >
                {item.label}{" "}
                {sortConfig.key === item.key &&
                  (sortConfig.direction === "asc" ? "↑" : "↓")}
              </Button>
            ))}
          </Box>
          {filteredSchools.length === 0 ? (
            <Typography sx={{ textAlign: "center", color: "#666", mt: 5 }}>
              {searchQuery
                ? "No matching schools found"
                : "No schools registered yet"}
            </Typography>
          ) : (
            <Grid container spacing={3}>
              {filteredSchools.map((school) => (
                <Grid item xs={12} sm={6} md={4} key={school._id}>
                  <Card
                    sx={{
                      boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
                      borderRadius: 2,
                      cursor: "pointer",
                      position: "relative",
                      "&:hover": {
                        transform: "scale(1.02)",
                        transition: "transform 0.2s",
                      },
                    }}
                    onClick={() =>
                      navigate("/school-details", {
                        state: { schoolId: school._id },
                      })
                    }
                  >
                    <CardContent>
                      <Typography
                        variant="h6"
                        sx={{ fontWeight: "bold", color: "#2c3e50", mb: 1 }}
                      >
                        {school.schoolName}
                      </Typography>
                      <Typography variant="body2" sx={{ color: "#666", mb: 1 }}>
                        Email: {school.email}
                      </Typography>
                      <Typography variant="body2" sx={{ color: "#666", mb: 1 }}>
                        Location: {school.location}, {school.village}
                      </Typography>
                      <Typography variant="body2" sx={{ color: "#666", mb: 1 }}>
                        Phone: {school.phoneNumber}
                      </Typography>
                      <Typography variant="body2" sx={{ color: "#666", mb: 1 }}>
                        Status: {school.isActive ? "Active" : "Inactive"}
                      </Typography>
                      <Typography variant="body2" sx={{ color: "#666" }}>
                        Registered:{" "}
                        {new Date(school.createdAt).toLocaleDateString()}
                      </Typography>
                      <ChevronRightIcon
                        sx={{
                          position: "absolute",
                          right: 15,
                          top: "50%",
                          mt: -1.5,
                          color: "#9C27B0",
                        }}
                      />
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          )}
        </Container>
      </Box>
      {isMobile && (
        <Paper
          sx={{
            position: "fixed",
            bottom: 0,
            left: 0,
            right: 0,
            zIndex: theme.zIndex.appBar,
            display: "flex",
            justifyContent: "space-around",
            alignItems: "center",
            py: 1,
            borderTop: "1px solid #eee",
          }}
          elevation={3}
        >
          {[
            { icon: <HomeIcon />, label: "Home", path: "/admin-dashboard" },
            {
              icon: <ChatIcon />,
              label: "Support",
              path: "/admin-support-dashboard",
            },
            {
              icon: <UploadIcon />,
              label: "Upload Video",
              path: "/admin-video-upload",
            },
            {
              icon: <VideoLibraryIcon />,
              label: "My Videos",
              path: "/admin-videos",
            },
            {
              icon: <NoteIcon />,
              label: "Resources",
              path: "/upload-teacher-resource",
            },
            {
              icon: <DeleteIcon />,
              label: "Deleted",
              path: "/deleted-schools",
            },
            { icon: <ExitToAppIcon />, label: "Logout", action: handleLogout },
          ].map((item, index) => (
            <Button
              key={index}
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                minWidth: "auto",
                color: "#666",
                textTransform: "none",
                p: 1,
              }}
              onClick={() =>
                item.action ? item.action() : navigate(item.path)
              }
            >
              <IconButton sx={{ color: "inherit" }}>{item.icon}</IconButton>
              <Typography variant="caption" sx={{ fontSize: "0.65rem" }}>
                {item.label}
              </Typography>
            </Button>
          ))}
        </Paper>
      )}
    </Box>
  );
};

export default AdminDashboard;
